// services/complaintApi.js
//
// The ops Complaints page API (tasks/complaints-to-fixes.md) on the shared secureApi instance — the auth
// header is attached automatically. Each call returns the parsed response body.
//
// Actions pass `preserveBackendError` so the page can read the backend's stable `code` (COMPLAINT_xxx) and
// translate it, instead of secureApi's generic text.

import { secureApi } from './secureApi';
import { TASK_WORKER_ROLES } from '../config/roleConfig';

const BASE = '/api/v1/complaints';
const KEEP_CODE = { preserveBackendError: true };

// The staff pickers' own filter (userApi.getStaffUsers): active or invited, never suspended.
const ACTIVE = ['active', 'invited'];

const complaintApi = {
  /** Every open complaint and those closed in the last 30 days, the counts, the repair-task default day. */
  getQueue: async () => (await secureApi.get(BASE)).data,

  /** Just the counts — the menu counter's poll. */
  getCounts: async () => (await secureApi.get(`${BASE}/counts`)).data,

  /** One complaint with its history. */
  getOne: async (id) => (await secureApi.get(`${BASE}/${id}`)).data,

  /** Where the «فتح شكوى» form starts — `{ kind: 'message'|'review'|'booking', id }`. */
  prefill: async ({ kind, id, topic }) => (await secureApi.get(`${BASE}/prefill`, { params: { kind, id, topic }, ...KEEP_CODE })).data,

  /** Open a complaint — `{ source, propertyId, category, severity, description }`. */
  create: async (body) => (await secureApi.post(BASE, body, KEEP_CODE)).data,

  /** The same problem raised again — attach this source to the open complaint. */
  addSource: async ({ id, source }) => (await secureApi.post(`${BASE}/${id}/sources`, { source }, KEEP_CODE)).data,

  /** Category / severity / description / accountable person (`accountableUserId`) / `waitingOn`. */
  edit: async ({ id, changes }) => (await secureApi.patch(`${BASE}/${id}`, changes, KEEP_CODE)).data,

  // guest-issue / compensation / prevention: kept on the server, off the screen since the three-step card
  // (tasks/followup-tasks-only.md Part B) — no client call.

  /** A repair task — `{ type, title, notes, assignedTo, scheduledDate }`. */
  createTask: async ({ id, ...task }) => (await secureApi.post(`${BASE}/${id}/tasks`, task, KEEP_CODE)).data,

  /** Close — `outcome`: no_task («لا تحتاج مهمة») · change · no_change; `note`: the one required line. */
  close: async ({ id, outcome, note }) => (await secureApi.post(`${BASE}/${id}/close`, { outcome, note }, KEEP_CODE)).data,

  reopen: async ({ id, reason }) => (await secureApi.post(`${BASE}/${id}/reopen`, { reason }, KEEP_CODE)).data,

  /**
   * Who can take a repair task (task workers) — one read of the users API; the role filter is applied by the
   * server. (The accountable-person picker left the screen with the three-step card.)
   */
  getPeople: async () => {
    const res = await secureApi.get('/api/v1/users', { params: { role: TASK_WORKER_ROLES.join(','), limit: 100 } });
    return { assignees: (res.data.users || []).filter(u => ACTIVE.includes(u.status)) };
  }
};

export default complaintApi;
